<script setup lang="ts">
import { inject, ref } from 'vue';
import '../office-ribbon.css';
import '../office-view.css';
import { i18nInjectionKey } from '../internal';
import { useNxOfficeRibbonCommands } from './ribbonCommands';

export interface NxOfficeRibbonTab {
  readonly id: string;
  readonly label: string;
  readonly title?: string;
  readonly contextual?: boolean;
  readonly disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    activeTab: string;
    tabs: readonly NxOfficeRibbonTab[];
    tablistLabel: string;
    quickAccessLabel?: string;
    /** Opt in to the document header of the shared document workspace. */
    workspace?: boolean;
    /** Compact sizing for dense document tools such as spreadsheets. */
    density?: 'comfortable' | 'compact';
    documentTitle?: string;
  }>(),
  { quickAccessLabel: 'Quick access' }
);

const emit = defineEmits<{ select: [id: string] }>();

function selectTab(tab: NxOfficeRibbonTab): void {
  if (!tab.disabled) emit('select', tab.id);
}

// The ribbon's buttons in the ⌘K palette, when the host asked for it
// (`provideNxOfficeRibbonCommands`, I-2348).
const root = ref<HTMLElement | null>(null);
const i18n = inject(i18nInjectionKey, null);
function tabCommandsLabel(tab: string): string {
  const key = 'common.office_ribbon.tab_commands';
  const text = i18n?.t(key, { tab });
  return text && text !== key ? text : `${tab}: Befehle`;
}
useNxOfficeRibbonCommands({
  root,
  activeTab: () => props.activeTab,
  tabs: () => props.tabs,
  select: (id) => emit('select', id),
  tabCommandsLabel,
});
</script>

<template>
  <header
    ref="root"
    class="nx-office-ribbon"
    :data-office-view="workspace ? 'ribbon' : undefined"
    :data-density="density"
  >
    <div v-if="workspace" class="nx-office-ribbon__documentbar">
      <div class="nx-office-ribbon__document">
        <slot name="documentIcon" />
        <div class="nx-office-ribbon__metadata">
          <strong :title="documentTitle">{{ documentTitle }}</strong>
          <div v-if="$slots.documentStatus" class="nx-office-ribbon__document-status">
            <slot name="documentStatus" />
          </div>
        </div>
      </div>
      <div v-if="$slots.documentActions" class="nx-office-ribbon__document-actions">
        <slot name="documentActions" />
      </div>
      <div
        v-if="$slots.quickAccess"
        class="nx-office-ribbon__quick-access nx-office-ribbon__header-tools"
        role="toolbar"
        :aria-label="quickAccessLabel"
      >
        <slot name="quickAccess" />
      </div>
    </div>
    <slot v-else name="titlebar" />
    <div class="nx-office-ribbon__tabs">
      <div class="nx-office-ribbon__tablist" role="tablist" :aria-label="tablistLabel">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="nx-office-ribbon__tab"
          :class="{
            'is-active': activeTab === tab.id,
            'nx-office-ribbon__tab--context': tab.contextual,
          }"
          role="tab"
          :title="tab.title"
          :disabled="tab.disabled"
          :aria-selected="activeTab === tab.id"
          @mousedown.prevent="selectTab(tab)"
          @keydown.enter.prevent="selectTab(tab)"
          @keydown.space.prevent="selectTab(tab)"
        >
          {{ tab.label }}
        </button>
      </div>
      <span v-if="!workspace" class="nx-office-ribbon__spacer" aria-hidden="true" />
      <div
        v-if="!workspace && $slots.quickAccess"
        class="nx-office-ribbon__quick-access"
        role="toolbar"
        :aria-label="quickAccessLabel"
      >
        <slot name="quickAccess" />
      </div>
    </div>
    <slot v-if="!workspace" />
    <div v-else class="nx-office-ribbon__commands">
      <slot />
    </div>
  </header>
</template>
